import { useEffect, useState } from "react";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import useApiRequest from "@/lib/hooks/use-request";
import { Button } from "@/ui-components/ui/button";
import { RefreshCw, CheckCircle, XCircle } from "lucide-react";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { useUser } from "@/lib/hooks/use-user";

const WordPressSync = () => {
  const { apiRequest } = useApiRequest();
  const { setTitle } = useBreadcrumb();
  const { user } = useUser();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (setTitle) {
      setTitle("WordPress Sync");
    }
  }, [setTitle]);

  const handleSyncAll = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    const res = await apiRequest<any>("interfaces/wordpress/sync", "POST", {});

    setSyncing(false);

    if (res.error) {
      console.error("Sync error:", res);
      setError(`Sync failed (Status: ${res.status}): ${(res.error as any)?.message || res.error || "Unknown error"}`);
    } else {
      setSyncResult(res.data);
    }
  };

  const handleSyncPipelines = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    const res = await apiRequest<any>("interfaces/wordpress/sync/pipelines", "POST", {});

    setSyncing(false);

    if (res.error) {
      setError((res.error as any)?.message || "Sync failed");
    } else {
      setSyncResult({ pipelines: res.data.synced, users: 0 });
    }
  };

  const handleSyncUsers = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    const res = await apiRequest<any>("interfaces/wordpress/sync/users", "POST", {});

    setSyncing(false);

    if (res.error) {
      setError((res.error as any)?.message || "Sync failed");
    } else {
      setSyncResult({ users: res.data.synced, pipelines: 0 });
    }
  };

  return (
    <div>
      <PageTitleContainer>
        <PageTitle>WordPress Synchronisation</PageTitle>
      </PageTitleContainer>

      <div className="p-6 max-w-4xl">
        {user && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
            <strong>Angemeldet als:</strong> {user.firstName} {user.lastName} ({user.role})
          </div>
        )}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">WordPress Daten synchronisieren</h2>
          <p className="text-gray-600 mb-6">
            Synchronisieren Sie Pipelines und Benutzer aus WordPress in das CRM-System.
          </p>

          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-2">Alles synchronisieren</h3>
              <p className="text-sm text-gray-600 mb-4">
                Synchronisiert alle Pipelines und Benutzer aus WordPress
              </p>
              <Button
                onClick={handleSyncAll}
                disabled={syncing}
                className="w-full sm:w-auto"
              >
                {syncing ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Synchronisiere...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} className="mr-2" />
                    Alles synchronisieren
                  </>
                )}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Nur Pipelines</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Synchronisiert nur die Pipelines
                </p>
                <Button
                  onClick={handleSyncPipelines}
                  disabled={syncing}
                  variant="outline"
                  className="w-full"
                >
                  {syncing ? (
                    <>
                      <RefreshCw size={16} className="mr-2 animate-spin" />
                      Synchronisiere...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} className="mr-2" />
                      Pipelines synchronisieren
                    </>
                  )}
                </Button>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">Nur Benutzer</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Synchronisiert nur die Benutzer
                </p>
                <Button
                  onClick={handleSyncUsers}
                  disabled={syncing}
                  variant="outline"
                  className="w-full"
                >
                  {syncing ? (
                    <>
                      <RefreshCw size={16} className="mr-2 animate-spin" />
                      Synchronisiere...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} className="mr-2" />
                      Benutzer synchronisieren
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Results */}
          {syncResult && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircle size={20} className="text-green-600 mr-2" />
                <h3 className="font-semibold text-green-800">Synchronisation erfolgreich</h3>
              </div>
              <div className="text-sm text-green-700">
                <p>Pipelines synchronisiert: {syncResult.pipelines || 0}</p>
                <p>Benutzer synchronisiert: {syncResult.users || 0}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center mb-2">
                <XCircle size={20} className="text-red-600 mr-2" />
                <h3 className="font-semibold text-red-800">Fehler bei der Synchronisation</h3>
              </div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-2">Hinweise</h3>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Pipelines werden automatisch erstellt, wenn sie in WordPress existieren</li>
              <li>Benutzer-Zuweisungen zu Pipelines werden aktualisiert</li>
              <li>Bestehende Daten werden nicht überschrieben, nur ergänzt</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordPressSync;
